import { z } from 'zod'

function rtdbNullable<T extends z.ZodTypeAny>(schema: T) {
  return schema
    .nullish()
    .transform((value): z.output<T> | null => value ?? null)
}

const nullableText = rtdbNullable(z.string().trim().max(160))
const nullableTimestamp = rtdbNullable(z.number().finite().positive())
const offsetAwareIso = rtdbNullable(
  z
    .string()
    .trim()
    .max(80)
    .regex(/T.*(?:Z|[+-]\d{2}:\d{2})$/)
)
const naiveWallTime = rtdbNullable(
  z
    .string()
    .trim()
    .max(80)
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/)
)

export const dashboardAppointmentSchema = z
  .object({
    appointmentId: z.string().trim().min(1).max(256),
    patientAuthorityId: rtdbNullable(
      z.string().regex(/^nhrlink_v1_[a-f0-9]{64}$/)
    ),
    patientDisplayName: rtdbNullable(z.string().trim().max(120)),
    patientPhone: rtdbNullable(z.string().trim().max(32)),
    scheduledAtMs: nullableTimestamp,
    scheduledAtIso: offsetAwareIso,
    scheduledEndMs: nullableTimestamp,
    scheduledEndIso: offsetAwareIso,
    scheduledWallTime: naiveWallTime,
    bookedAtMs: nullableTimestamp,
    clinicTimeZone: rtdbNullable(z.string().trim().max(80)),
    providerId: nullableText,
    providerName: nullableText,
    appointmentTypeId: nullableText,
    appointmentTypeName: nullableText,
    operatoryId: nullableText,
    operatoryName: nullableText,
    source: z.enum(['sms', 'widget', 'voice', 'dashboard', 'unknown']),
    status: z.enum(['scheduled', 'confirmed', 'cancelled', 'unknown']),
    // Flio bookings only: whether the clinic's practice system holds it yet.
    pmsState: rtdbNullable(z.enum(['confirmed', 'pending'])),
    confirmedAtMs: nullableTimestamp,
    cancelledAtMs: nullableTimestamp,
    cancellationReason: rtdbNullable(z.string().trim().max(240)),
    temporalConfidence: z.enum(['canonical', 'legacy-wall-time', 'invalid']),
    updatedAtMs: z.number().finite().positive(),
  })
  .strict()

export type RawDashboardAppointment = z.infer<typeof dashboardAppointmentSchema>

export const appointmentCursorSchema = z.object({
  scheduledAtMs: z.number().finite().positive(),
})
